import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import '@/AuditPage.css';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_only-creator/artifacts/90vlzise_original%20color%20logo%20%281%29.png';
const HERO_VIDEO_URL = 'https://customer-assets.emergentagent.com/job_3be01be0-0edd-40df-9e00-24f9aed3e403/artifacts/zm7a9z9m_Find%20Your%20%27Only%20Statement%27_%20Be%20the%202%20Dollar%20Bill.mp4';
const CALENDLY_DISCOVERY = 'https://calendly.com/bradholcman/book-your-free-15-minute-brand-positioning-sess-clone?month=2026-03';
const GHL_CHECKOUT_URL = 'https://audit.2dollarbillbrand.com/checkout-page';

const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

export default function AuditPage() {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const exitTriggered = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    trackEvent('audit_page_viewed', { page_title: 'audit_sales_page' });

    // Exit-intent detection (desktop: mouse leaves viewport top)
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !exitTriggered.current) {
        exitTriggered.current = true;
        setShowEmailModal(true);
        trackEvent('exit_intent_triggered', { page: 'audit' });
      }
    };

    // Delay-based fallback for mobile (show after 45 seconds)
    const mobileTimer = setTimeout(() => {
      if (!exitTriggered.current) {
        exitTriggered.current = true;
        setShowEmailModal(true);
        trackEvent('delay_popup_triggered', { page: 'audit' });
      }
    }, 45000);

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(mobileTimer);
    };
  }, []);

  const handleCtaClick = (location) => {
    trackEvent('audit_cta_clicked', { location });
  };

  const faqItems = [
    {
      question: 'Is this a group session or 1-on-1?',
      answer: 'This is a private 1-on-1 session with Brad Holcman. No group calls, no cohort model. The full 60 minutes is dedicated entirely to you, your positioning, and your business.',
    },
    {
      question: 'Who is this for?',
      answer: 'The audit is designed for professionals who already have real expertise but struggle to explain what makes them different. Typical clients include consultants, coaches, founders, executives, real estate investors, and experts building authority in their space.',
    },
    {
      question: 'What happens after I book?',
      answer: 'After you complete your payment, you will receive a short pre-session form and a link to schedule your session through Calendly. Brad reviews your form and quiz answers before the call so you can start working immediately.',
    },
    {
      question: 'What do I walk away with?',
      answer: 'Within 48 hours of your session, you receive a 15-17 page positioning PDF that includes your refined ONLY Statement, five statement variations, your brand thread, ideal client profile, a 60-second elevator pitch, platform-specific headlines, competitive differentiation analysis, content direction, and strategic recommendations.',
    },
    {
      question: 'How does the credit toward the $2 Bill Brand Authority Accelerator work?',
      answer: 'If you decide to join the $2 Bill Brand Authority Accelerator (a 12-week, 1-on-1 intensive program), the full $797 you invested in the audit is credited toward the program fee. You are not charged twice.',
    },
    {
      question: 'Why $797?',
      answer: 'Serious positioning requires serious commitment. The people who get the best results show up invested. Typical brand strategists charge $3,000-$5,000 for positioning work. The audit delivers real clarity in 60 minutes. $797 is the price of your full attention and mine.',
    },
  ];

  const testimonials = [
    {
      name: 'John Swann',
      role: 'Entrepreneur',
      quote: 'Yes, 100% absolutely! Working with Brad was wonderful. From the beginning Brad listened intently as I shared my story and he quickly identified ways to better position, market, and advocate my company against competitors!',
    },
    {
      name: 'Sharon Thornburg',
      role: 'Coach',
      quote: 'My session with Brad was emotionally changing for me. I have been doubting what my gift is for working with people. He helped me see that I do have something of value to share with others. I felt seen and heard!',
    },
    {
      name: 'Annette Dahl',
      role: 'Consultant',
      quote: 'I left with affirmation that I was in the right place, and a challenge to spread my wings beyond my comfort zone to offer authority. Brad was able to help me verbalize and own it. The final document captured all that Brad had pointed out.',
    },
  ];

  return (
    <div className="audit-page" data-testid="audit-page">
      <div className="audit-container">

        {/* Logo */}
        <div className="audit-logo-wrap" data-testid="audit-logo">
          <Link to="/">
            <img src={LOGO_URL} alt="$2 Bill Brand Logo" className="audit-logo" />
          </Link>
        </div>

        {/* ========== SECTION 1 - HERO ========== */}
        <section className="audit-section audit-hero" data-testid="audit-hero">
          <h1 className="audit-hero-headline" data-testid="audit-hero-headline">
            You're not buying a strategy session.<br />You're buying the moment when you finally have the sentence.
          </h1>
          <p className="audit-hero-sub">
            You have your draft of your ONLY statement. The ONLY Statement Audit turns that ONLY statement into a positioning strategy you can actually use.
          </p>

          <div className="audit-hero-video" data-testid="audit-hero-video">
            <video autoPlay muted loop playsInline controls preload="metadata">
              <source src={HERO_VIDEO_URL} type="video/mp4" />
            </video>
          </div>

          <div className="audit-hero-cta-block">
            <a
              href="/checkout"
              className="audit-cta-btn"
              data-testid="hero-cta-btn"
              onClick={() => handleCtaClick('hero')}
            >
              Book My ONLY Statement Audit
            </a>
            <p className="audit-cta-price-line" data-testid="audit-cta-price-line">
              Investment: $797 - Includes 60-minute session + 15-17 page positioning PDF
            </p>
            <p className="audit-free-call-prompt" data-testid="audit-free-call-prompt-hero">
              Not ready to commit? Book a free 15-minute conversation first - no pitch, no pressure.
            </p>
            <a
              href={CALENDLY_DISCOVERY}
              target="_blank"
              rel="noopener noreferrer"
              className="audit-cta-btn audit-cta-btn-secondary"
              data-testid="hero-free-call-btn"
              onClick={() => handleCtaClick('hero_free_call')}
            >
              Book a Free 15-Minute Call
            </a>
            <p className="audit-scarcity-line" data-testid="audit-scarcity">Brad currently has a limited number of audit slots open each month.</p>
            <div className="audit-hero-details" data-testid="audit-hero-details">
              <p className="audit-hero-detail-line"><strong>ONLY Statement Audit</strong></p>
              <p className="audit-hero-detail-line">60-minute 1-on-1 session with Brad Holcman</p>
              <p className="audit-hero-detail-line">15-17 page positioning PDF delivered within 48 hours</p>
            </div>
            <div className="audit-trust-row" data-testid="audit-trust-row">
              <div className="audit-trust-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                <span>22 years at the greenlight table</span>
              </div>
              <div className="audit-trust-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                <span>More than 90 audits delivered</span>
              </div>
              <div className="audit-trust-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
                <span>Credit toward $2 Bill Brand Authority Accelerator</span>
              </div>
            </div>
          </div>
        </section>

        {/* ========== SECTION 2 - THE PROBLEM ========== */}
        <section className="audit-section audit-card" data-testid="audit-problem">
          <h2 className="audit-section-headline">Here is what it is costing you to not have the sentence.</h2>
          <p className="audit-body">Most professionals know they are good at what they do. But when someone asks "What do you do?" they give a paragraph instead of a sentence. The words are accurate but not memorable. People understand what you said but cannot repeat it to someone else.</p>

          <div className="audit-problem-grid" data-testid="audit-problem-grid">
            <div className="audit-problem-col">
              <h4 className="audit-problem-label">In Conversation</h4>
              <p>You give a paragraph when someone asks what you do. They understand the words but cannot repeat it.</p>
            </div>
            <div className="audit-problem-col">
              <h4 className="audit-problem-label">In Your Bio</h4>
              <p>LinkedIn headline, website bio, speaker bio all say similar vague things.</p>
            </div>
            <div className="audit-problem-col">
              <h4 className="audit-problem-label">In the Market</h4>
              <p>You know you are better than most people in your space but people cannot see the difference.</p>
            </div>
          </div>

          <p className="audit-body audit-body-closing">This is not a talent problem. This is a positioning problem. And it is costing you clients, opportunities, and momentum every single day.</p>
        </section>

        {/* ========== SECTION 3 - THE SOLUTION ========== */}
        <section className="audit-section audit-card" data-testid="audit-solution">
          <h2 className="audit-section-headline">The ONLY Statement is not a tagline. It is the truth you have never said out loud.</h2>
          <p className="audit-body">Your ONLY Statement is the one sentence that captures what makes you impossible to ignore, impossible to compete with, and impossible to forget. It is not a marketing line. It is the thing that is already true about you - you just have not had the words for it yet.</p>

          <div className="audit-format-box" data-testid="audit-format-box">
            <p className="audit-format-label">Example (from a real audit client):</p>
            <p className="audit-format-example">"I'm the only <span>financial advisor</span> who <span>spent 12 years as an IRS auditor</span> - so <span>business owners can finally stop treating taxes as a surprise and start using them as strategy</span>."</p>
            <div className="audit-format-structure">
              <p className="audit-format-structure-label">Structure of the ONLY Statement:</p>
              <p className="audit-format-structure-line">The only <span>type of expert or role</span></p>
              <p className="audit-format-structure-line">who <span>solves a problem in a uniquely specific way</span></p>
              <p className="audit-format-structure-line">so that <span>a clear result happens for the client</span>.</p>
            </div>
          </div>

          <p className="audit-body">Think of the $2 bill. It is worth exactly $2 - no more than any other bill. But people keep it. They remember it. They talk about it. Not because it is worth more, but because it is different. Your ONLY Statement does the same thing for your personal brand.</p>
        </section>

        {/* ========== SECTION 4 - WHAT YOU GET ========== */}
        <section className="audit-section audit-card" data-testid="audit-deliverables">
          <h2 className="audit-section-headline">What You Get In Your ONLY Statement Audit</h2>

          <div className="audit-deliverables-grid" data-testid="audit-deliverables-grid">
            <div className="audit-deliverable-card">
              <div className="audit-deliverable-num">1</div>
              <h4>60-Minute 1-on-1 Session with Brad</h4>
              <p>Not a sales call. A real working session where Brad listens, asks the questions no one else asks, and finds the pattern you have been living but never consciously named.</p>
            </div>

            <div className="audit-deliverable-card">
              <div className="audit-deliverable-num">2</div>
              <h4>Strategic Deep Dive</h4>
              <ul>
                <li>Core story</li>
                <li>Category positioning</li>
                <li>Content strategy</li>
                <li>Business model direction</li>
                <li>90-day plan</li>
              </ul>
            </div>

            <div className="audit-deliverable-card">
              <div className="audit-deliverable-num">3</div>
              <h4>15-17 Page Positioning PDF</h4>
              <ul>
                <li>Refined ONLY Statement</li>
                <li>Five statement variations</li>
                <li>Brand thread</li>
                <li>Ideal client profile</li>
                <li>Elevator pitch</li>
                <li>Platform headlines</li>
                <li>Competitive differentiation</li>
                <li>Content direction</li>
                <li>Strategic recommendations</li>
              </ul>
            </div>

            <div className="audit-deliverable-card">
              <div className="audit-deliverable-num">4</div>
              <h4>Credit Toward $2 Bill Brand Authority Accelerator</h4>
              <p>If you decide to go further and join the 12-week $2 Bill Brand Authority Accelerator, your full audit investment applies toward the program fee. You are not charged twice.</p>
            </div>
          </div>
        </section>

        {/* ========== SECTION 5 - ABOUT BRAD ========== */}
        <section className="audit-section audit-card audit-bio" data-testid="audit-bio">
          <div className="audit-bio-inner">
            <img
              src="https://customer-assets.emergentagent.com/job_category-of-one/artifacts/huv8apu6_FM925_Black_web_065.jpg"
              alt="Brad Holcman"
              className="audit-bio-photo"
              data-testid="audit-bio-photo"
            />
            <div className="audit-bio-text">
              <h3>Brad Holcman</h3>
              <p className="audit-bio-creds">4-time Emmy-nominated television executive</p>
              <p>22 years at the greenlight table - deciding in 3 seconds whether something had a hook. Now I apply that same lens to your positioning.</p>
            </div>
          </div>
          <blockquote className="audit-bio-quote" data-testid="audit-bio-quote">
            "A $2 bill is worth exactly $2. But people keep it because it is different."
            <cite>- Brad Holcman</cite>
          </blockquote>
        </section>

        {/* ========== SECTION 6 - TESTIMONIALS ========== */}
        <section className="audit-section audit-card" data-testid="audit-testimonials">
          <h2 className="audit-section-headline">What Others Are Saying</h2>

          <div className="audit-text-testimonials" data-testid="audit-text-testimonials">
            {testimonials.map((t, idx) => (
              <div className="audit-testimonial-card" key={idx} data-testid={`audit-testimonial-${idx}`}>
                <p className="audit-testimonial-quote">"{t.quote}"</p>
                <div className="audit-testimonial-meta">
                  <span className="audit-testimonial-name">- {t.name}</span>
                  <span className="audit-testimonial-role">{t.role}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ========== SECTION 7 - MID PAGE CTA ========== */}
        <section className="audit-section audit-mid-cta" data-testid="audit-mid-cta">
          <p className="audit-mid-cta-text">You are not buying a strategy session.<br />You are buying the moment when you finally have the sentence.</p>
          <a
            href="/checkout"
            className="audit-cta-btn"
            data-testid="mid-cta-btn"
            onClick={() => handleCtaClick('mid_page')}
          >
            Book My ONLY Statement Audit
          </a>
        </section>

        {/* ========== SECTION 8 - HOW IT WORKS ========== */}
        <section className="audit-section audit-card" data-testid="audit-how-it-works">
          <h2 className="audit-section-headline">How It Works</h2>
          <div className="audit-steps" data-testid="audit-steps">
            {[
              { num: 1, text: 'Book and pay for the audit.' },
              { num: 2, text: 'Receive a short pre-session form.' },
              { num: 3, text: 'Schedule your session through Calendly.' },
              { num: 4, text: '60 minute strategy session with Brad.' },
              { num: 5, text: 'Receive your 15-17 page positioning PDF within 48 hours.' },
            ].map((step) => (
              <div className="audit-step" key={step.num} data-testid={`audit-step-${step.num}`}>
                <div className="audit-step-num">{step.num}</div>
                <p className="audit-step-text">{step.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ========== SECTION 9 - FAQ ========== */}
        <section className="audit-section audit-card" data-testid="audit-faq">
          <h2 className="audit-section-headline">Frequently Asked Questions</h2>
          <div className="audit-faq-list" data-testid="audit-faq-list">
            {faqItems.map((faq, idx) => (
              <div key={idx} className={`audit-faq-item ${expandedFaq === idx ? 'expanded' : ''}`} data-testid={`audit-faq-${idx}`}>
                <button
                  className="audit-faq-question"
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  data-testid={`audit-faq-q-${idx}`}
                >
                  <span>{faq.question}</span>
                  <span className="audit-faq-icon">{expandedFaq === idx ? '\u2212' : '+'}</span>
                </button>
                {expandedFaq === idx && (
                  <div className="audit-faq-answer" data-testid={`audit-faq-a-${idx}`}>
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ========== SECTION 10 - FINAL CTA ========== */}
        <section className="audit-section audit-final-cta" data-testid="audit-final-cta">
          <h2 className="audit-final-headline">Ready To Become The ONLY?</h2>
          <p className="audit-final-body">You are not buying a service. You are becoming the person who finally has the words. The quiz gave you a first look. The audit is where we finish the sentence.</p>
          <div className="audit-final-buttons">
            <a
              href="/checkout"
              className="audit-cta-btn"
              data-testid="final-cta-btn"
              onClick={() => handleCtaClick('final')}
            >
              Book My ONLY Statement Audit
            </a>
            <Link to="/" className="audit-secondary-btn" data-testid="back-to-quiz-btn">
              Back to Quiz Results
            </Link>
          </div>
        </section>

        {/* ========== SECTION 11 - DISCOVERY CALL CTA ========== */}
        <section className="audit-section audit-card audit-discovery" data-testid="audit-discovery">
          <h2 className="audit-section-headline audit-discovery-headline">Not ready to commit today?</h2>
          <p className="audit-body">If you are not sure the audit is right for you yet, book a free 15-minute call with Brad. No pitch, no pressure - just a conversation to see if this is the right next step for you.</p>
          <div className="audit-discovery-cta">
            <a
              href={CALENDLY_DISCOVERY}
              target="_blank"
              rel="noopener noreferrer"
              className="audit-secondary-btn"
              data-testid="discovery-call-btn"
              onClick={() => handleCtaClick('discovery_call')}
            >
              Book a Free 15-Minute Call with Brad
            </a>
          </div>
        </section>

        {/* ========== FOOTER - Social Links ========== */}
        <footer className="audit-footer" data-testid="audit-footer">
          <div className="audit-social-follow">
            <p className="audit-social-title">Follow Brad Holcman</p>
            <div className="audit-social-icons">
              <a href="https://www.facebook.com/groups/750710407319175" target="_blank" rel="noopener noreferrer" className="audit-social-link" title="Facebook">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.linkedin.com/in/brad-holcman/" target="_blank" rel="noopener noreferrer" className="audit-social-link" title="LinkedIn">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="https://www.youtube.com/@BradleyHolcman" target="_blank" rel="noopener noreferrer" className="audit-social-link" title="YouTube">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href="https://www.instagram.com/bradneedsabs/" target="_blank" rel="noopener noreferrer" className="audit-social-link" title="Instagram">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/></svg>
              </a>
              <a href="https://www.tiktok.com/@bholcman" target="_blank" rel="noopener noreferrer" className="audit-social-link" title="TikTok">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
              </a>
            </div>
          </div>
        </footer>

      </div>

      {/* ========== BOOKING POPUP ========== */}
      {showEmailModal && (
        <div className="audit-modal-overlay" data-testid="booking-modal-overlay" onClick={() => setShowEmailModal(false)}>
          <div className="audit-modal" data-testid="booking-modal" onClick={(e) => e.stopPropagation()}>
            <button className="audit-modal-close" onClick={() => setShowEmailModal(false)} data-testid="booking-modal-close">&times;</button>
            <h3 className="audit-modal-title">Stop Sounding Like Everyone Else</h3>
            <p className="audit-modal-body">
              Book your private 1-on-1 ONLY Statement Audit with Brad.
            </p>
            <p className="audit-modal-body" style={{ marginBottom: '0.5rem' }}>
              You'll get one full hour to uncover what makes you different, sharpen your message, and walk away with clearer words people actually remember.
            </p>
            <p className="audit-modal-pricing" data-testid="booking-modal-pricing">
              <span className="audit-modal-price-new">$797</span>
            </p>
            <a
              href={GHL_CHECKOUT_URL}
              className="audit-cta-btn audit-modal-submit"
              data-testid="booking-modal-cta"
              onClick={() => trackEvent('popup_booking_cta_clicked', { source: 'exit_intent' })}
            >
              Book My 1-Hour Audit
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
